import React, { useState } from "react";
import { X, Plus, Trash2, FileText, IndianRupee } from "lucide-react";
import api from "../api/axiosInstance";

const GenerateInvoiceModal = ({ project, onClose, onRefresh }) => {
  const [invoiceType, setInvoiceType] = useState("Flat");
  const [gstRate, setGstRate] = useState(18);
  const [items, setItems] = useState([{ description: "", amount: "" }]);
  const [flatAmount, setFlatAmount] = useState("");

  const calculateTotals = () => {
    const taxable =
      invoiceType === "Flat"
        ? parseFloat(flatAmount || 0)
        : items.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);

    const totalGst = (taxable * gstRate) / 100;
    const cgst = totalGst / 2;
    const sgst = totalGst / 2;

    return {
      taxable_amount: taxable,
      cgst_amount: cgst,
      sgst_amount: sgst,
      total_amount: taxable + totalGst,
    };
  };

  const { taxable_amount, cgst_amount, sgst_amount, total_amount } =
    calculateTotals();

  const handleSave = async () => {
    const payload = {
      project_id: project.id,
      client_id: project.client_id, // Assuming project object has client_id
      invoice_date: new Date().toISOString().split("T")[0],
      gst_rate: gstRate,
      taxable_amount,
      cgst_amount,
      sgst_amount,
      total_amount,
      invoice_type: invoiceType,
      items: invoiceType === "Itemized" ? items : null,
    };

    try {
      const res = await api.post("save_and_generate_invoice.php", payload);
      if (res.data.status === "success") {
        // Use the blob method for the PDF to ensure token is sent
        const pdfRes = await api.get(
          `view_invoice_pdf.php?invoice_id=${res.data.invoice_id}`,
          { responseType: "blob" },
        );
        const url = window.URL.createObjectURL(new Blob([pdfRes.data]));
        window.open(url);
        onRefresh();
        onClose();
      }
    } catch (err) {
      alert("Error generating invoice. Check console.");
    }
  };

  return (
    <div className="fixed inset-0 z-[300] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-4xl rounded-[48px] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="p-8 border-b border-slate-100 flex justify-between items-center">
          <h2 className="text-3xl font-black italic uppercase tracking-tighter">
            Tax Invoice Generator
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-full"
          >
            <X />
          </button>
        </div>

        <div className="p-8 overflow-y-auto space-y-6">
          <div className="flex gap-4 p-1.5 bg-slate-100 rounded-2xl w-fit">
            {["Flat", "Itemized"].map((mode) => (
              <button
                key={mode}
                onClick={() => setInvoiceType(mode)}
                className={`px-6 py-2 rounded-xl font-black text-[10px] uppercase transition-all ${invoiceType === mode ? "bg-white shadow-sm text-indigo-600" : "text-slate-400"}`}
              >
                {mode} Mode
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="bg-slate-50 p-6 rounded-3xl">
              <label className="text-[10px] font-black uppercase text-slate-400">
                GST Rate (%)
              </label>
              <select
                className="w-full mt-2 bg-transparent font-black text-xl outline-none"
                value={gstRate}
                onChange={(e) => setGstRate(e.target.value)}
              >
                <option value="0">0% (Exempt)</option>
                <option value="5">5% (Construction)</option>
                <option value="12">12% (Contracting)</option>
                <option value="18">18% (Interior Work)</option>
                <option value="28">28% (Luxury)</option>
              </select>
            </div>

            {invoiceType === "Flat" && (
              <div className="bg-slate-50 p-6 rounded-3xl border-2 border-indigo-100">
                <label className="text-[10px] font-black uppercase text-slate-400">
                  Taxable Base Amount
                </label>
                <input
                  type="number"
                  className="w-full mt-2 bg-transparent font-black text-2xl text-indigo-600 outline-none"
                  placeholder="0.00"
                  value={flatAmount}
                  onChange={(e) => setFlatAmount(e.target.value)}
                />
              </div>
            )}
          </div>

          {invoiceType === "Itemized" && (
            <div className="space-y-3">
              <label className="text-[10px] font-black uppercase text-slate-400 ml-2">
                Work Items Breakdown
              </label>
              {items.map((item, index) => (
                <div
                  key={index}
                  className="flex gap-4 items-center bg-slate-50 p-2 rounded-2xl"
                >
                  <input
                    className="flex-1 bg-white p-4 rounded-xl font-bold text-sm"
                    placeholder="Work Description"
                    value={item.description}
                    onChange={(e) => {
                      const n = [...items];
                      n[index].description = e.target.value;
                      setItems(n);
                    }}
                  />
                  <input
                    type="number"
                    className="w-32 bg-white p-4 rounded-xl font-black text-sm"
                    placeholder="Amt"
                    value={item.amount}
                    onChange={(e) => {
                      const n = [...items];
                      n[index].amount = e.target.value;
                      setItems(n);
                    }}
                  />
                  <button
                    onClick={() =>
                      setItems(items.filter((_, i) => i !== index))
                    }
                    className="p-2 text-rose-500"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              ))}
              <button
                onClick={() =>
                  setItems([...items, { description: "", amount: "" }])
                }
                className="w-full py-4 border-2 border-dashed border-slate-200 rounded-2xl text-[10px] font-black uppercase text-slate-400 hover:border-indigo-300 hover:text-indigo-500 transition-all"
              >
                + Add Description Line
              </button>
            </div>
          )}
        </div>

        <div className="p-8 bg-slate-900 text-white flex justify-between items-center">
          <div className="flex gap-6">
            <div>
              <p className="text-[8px] font-black uppercase opacity-50">
                Taxable
              </p>
              <p className="font-bold">₹{taxable_amount.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-[8px] font-black uppercase opacity-50">
                CGST/SGST
              </p>
              <p className="font-bold">₹{cgst_amount.toLocaleString()} x2</p>
            </div>
            <div className="border-l border-white/20 pl-6">
              <p className="text-[8px] font-black uppercase opacity-50 text-indigo-400">
                Total Payable
              </p>
              <p className="text-2xl font-black italic text-indigo-400">
                ₹{total_amount.toLocaleString()}
              </p>
            </div>
          </div>
          <button
            onClick={handleSave}
            className="bg-indigo-600 hover:bg-indigo-500 text-white px-10 py-4 rounded-2xl font-black text-xs uppercase shadow-xl flex items-center gap-2"
          >
            <FileText size={18} /> Save & Generate
          </button>
        </div>
      </div>
    </div>
  );
};

export default GenerateInvoiceModal;
